import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { Badge, Button, Spinner, Tabs, TabsList, TabsTrigger } from '@monksflow/monks-ui';
import { AgentConfigurationService, AgentsService } from '@inference.engine/api-client/web';
import { useToolConfigScopes } from '@/features/admin/model/useToolConfigScopes';
import { ScopeTab, WorkspaceScopeTab } from '@/features/admin/ui/ToolConfigPage';
import { KbSelectWidget, KbMultiSelectWidget } from '@/features/knowledge-bases';
import { ROUTES } from '@/shared/config/routes';
import { ADMIN_QUERY_KEYS } from '@/features/admin/api/query-keys';
import { inferenceHttp } from '@/lib/api/inference-client';
import { useAuth } from '@/entities/user';
import { getAdminToolsListDisplayName } from '@/features/admin/lib/canonicalAdminToolLabels';

import { AdminPageShell } from '../AdminPageShell';
import { useTranslation } from 'react-i18next';

type ScopeLevel = 'organization' | 'workspace';

export function ToolConfigPage() {
  const { t } = useTranslation('admin');

  const { agentId, toolId } = useParams<{ agentId: string; toolId: string }>();
  const [searchParams] = useSearchParams();
  const ownerAgentId = searchParams.get('ownerAgentId') ?? agentId!;
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ScopeLevel>('organization');

  const workspaces = useAuth((state) => state.signedInUser.value?.workspaces) ?? [];

  const agentQuery = useQuery({
    queryKey: [...ADMIN_QUERY_KEYS.tools.toolConfig, agentId],
    queryFn: async () => {
      const response = await AgentsService.getAgentById({
        client: inferenceHttp,
        path: { id: agentId! },
      });
      return response.data!;
    },
    enabled: !!agentId,
  });

  const schemasQuery = useQuery({
    queryKey: ADMIN_QUERY_KEYS.tools.schemas,
    queryFn: async () => {
      const { data } = await AgentConfigurationService.getToolSchemas({
        client: inferenceHttp,
      });
      return data ?? [];
    },
  });

  const {
    scopes,
    isLoading: scopesLoading,
    save,
    isSaving,
    remove,
    isRemoving,
    bulkSave,
    isBulkSaving,
  } = useToolConfigScopes({
    toolId: toolId!,
    agentId: ownerAgentId,
  });

  const agent = agentQuery.data;
  const toolSchema = schemasQuery.data?.find((s) => s.toolId === toolId);

  // Handlers — always with agentId
  // Errors are handled by onError callbacks in useToolConfigScopes mutations
  const handleOrgSave = (data: Record<string, unknown>) => {
    void save({ data });
  };

  const handleOrgDelete = () => {
    void remove({});
  };

  const handleWorkspaceSave = (workspaceId: string, data: Record<string, unknown>) => {
    void save({ data, workspaceId });
  };

  const handleWorkspaceRemove = (workspaceId: string) => {
    void remove({ workspaceId });
  };

  const handleWorkspaceBulkSave = (workspaceIds: string[], data: Record<string, unknown>) => {
    void bulkSave({ workspaceIds, data });
  };

  if (agentQuery.isLoading || scopesLoading || !toolSchema) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24">
        <p className="text-base leading-6 text-muted-foreground">
          {t('admin.toolConfigPage.agentNotFound')}
        </p>
        <Button type="button" variant="outline" onClick={() => void navigate(ROUTES.ADMIN_TOOLS)}>
          {t('admin.toolConfigPage.backToTools')}
        </Button>
      </div>
    );
  }

  // `ownsBodyInset`: the padded scroll region below is this page's inset in both frames,
  // and it is exactly the one the shell would otherwise add.
  return (
    <AdminPageShell
      title={toolSchema.toolName}
      parents={[
        { label: t('admin.adminLayout.tools'), href: ROUTES.ADMIN_TOOLS },
        {
          label: getAdminToolsListDisplayName(agent.id, agent.name),
          href: ROUTES.ADMIN_AGENT_CONFIG(agentId!),
        },
      ]}
      ownsBodyInset
    >
      <div className="flex-1 px-6 py-6 overflow-y-auto">
        <div className="flex flex-col gap-6">
          {/* Tabs — simplified to 2 levels */}
          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ScopeLevel)}>
            <TabsList className="justify-start">
              {/* eslint-disable-next-line i18next/no-literal-string -- tab state value compared against activeTab */}
              <TabsTrigger value="organization">
                {t('admin.toolConfigPage.organization')}
                {scopes?.orgConfig && (
                  <Badge variant="positive" roundness="full">
                    {t('admin.toolConfigPage.configured')}
                  </Badge>
                )}
              </TabsTrigger>
              {/* eslint-disable-next-line i18next/no-literal-string -- tab state value compared against activeTab */}
              <TabsTrigger value="workspace">
                {t('admin.toolConfigPage.workspace')}
                {(scopes?.workspaceConfigs.length ?? 0) > 0 && (
                  <Badge variant="positive" roundness="full">
                    {t('admin.toolConfigPage.countConfigured', {
                      configuredCount: scopes!.workspaceConfigs.length,
                    })}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {activeTab === 'organization' && scopes && (
            <ScopeTab
              toolId={toolId!}
              jsonSchema={toolSchema.jsonSchema}
              existingConfig={scopes.orgConfig}
              description={t('admin.toolConfigPage.configurationForTheAgentAcross', {
                agentName: agent.name,
              })}
              onSave={handleOrgSave}
              onDelete={handleOrgDelete}
              isSaving={isSaving}
              isRemoving={isRemoving}
              kbSelectWidget={KbSelectWidget}
              kbMultiSelectWidget={KbMultiSelectWidget}
            />
          )}

          {activeTab === 'workspace' && scopes && (
            <WorkspaceScopeTab
              toolId={toolId!}
              jsonSchema={toolSchema.jsonSchema}
              configs={scopes.workspaceConfigs}
              allWorkspaces={workspaces}
              fullyConfiguredWorkspaceIds={scopes.workspaceConfigs
                .map((c) => c.workspaceId)
                .filter((id): id is string => !!id)}
              description={t('admin.toolConfigPage.workspaceSpecificOverridesForThe', {
                agentName: agent.name,
              })}
              fallbackLabel={t('admin.toolConfigPage.allWorkspacesWillUseThe')}
              onSave={handleWorkspaceSave}
              onRemove={handleWorkspaceRemove}
              onBulkSave={handleWorkspaceBulkSave}
              isSaving={isSaving}
              isRemoving={isRemoving}
              isBulkSaving={isBulkSaving}
              kbSelectWidget={KbSelectWidget}
              kbMultiSelectWidget={KbMultiSelectWidget}
            />
          )}
        </div>
      </div>
    </AdminPageShell>
  );
}
